import {EventMileage} from "@QUERIES/prdt/event/useEventMileage";
import dayjs from "dayjs";

type TypeFlag = {
	$type: "product" | "code" | "type" | "promotion" | "brand" | "emp" | "process";
	$flag: string;
	text: string;
};

/* product 데이터 */
const hashTagManufacture = (keyWordList: string, splitCount: number): string =>
	keyWordList
		?.split(",", splitCount)
		?.map(str => `#${str}`)
		?.join(" ");

const goodsTpLabel = (goodsTp: string): null | Array<string> => {
	const classType = {
		P: ["pkg", "해외패키지"],
		D: ["dmt", "국내여행"],
		K: ["semi", "세미패키지"],
		F: ["fit", "에어텔"],
		H: ["honey", "허니문"],
		G: ["golf", "골프"],
		C: ["cruise", "크루즈"],
		R: ["pilgrimage", "성지순례"],
		T: ["air", "항공권"],
		S: ["theme", "테마상품"],
		M: ["biz", "기업단체"],
		L: ["hotel", "호텔"],
		// E: ["ticket", "입장권"],
		// J: ["local", "현지투어"],
	};

	if (!goodsTp || !classType[goodsTp]) return null;
	return classType[goodsTp];
};

const goodsFlag = (type: string): TypeFlag => {
	const flagProps = {
		live: {$type: "promotion", $flag: "yellowlive", text: "옐로LIVE"},
		// pang: {$type: "promotion", $flag: "yellowpangdeal", text: "옐로팡딜"},
	};

	if (!type || !flagProps[type]) return null;
	return flagProps[type];
};

const evProgDiviLabel = (evProgDivi: string): string => {
	if (!evProgDivi) return null;
	const classType = {
		key03: "impending", // 마감임박
		key05: "deadline", // 예약마감
		key08: "planning", // 출발예정
		key07: "decided", // 출발확정
		key11: "decided", // 100% 출발확정
	};
	if (!evProgDivi || !classType[evProgDivi]) return null;
	return classType[evProgDivi];
};

const goodsBrandCdLabel = (goodsBrandCd: string): null | Array<string> => {
	const classType = {
		30: ["premium", "TOP PICK"],
		40: ["light", "라이트팩"],
		60: ["percent", "100% 출발"],
		// 50: ["signature", "시그니처 유럽"],
	};

	if (!goodsBrandCd || !classType[goodsBrandCd]) return null;
	return classType[goodsBrandCd];
};

const emphasisCdLabel = (emphasisCd: string): null | Array<string> => {
	const classType = {
		"1": "특가",
		// "2": "실속",
		// "3": "품격",
		// "4": "프리미엄",
		"5": "주말상품",
		// "6": "땡처리",
		"7": "선착순",
		"8": "특급호텔",
		"9": "초특급호텔",
		A: "선발권특가",
		B: "연휴",
		C: "선발권",
		D: "집중",
		E: "노옵션/노쇼핑",
		F: "할인프로모션",
		G: "비즈니스석",
		H: "담당자추천",
		I: "노쇼핑",
		J: "선착순특가",
		K: "연휴특선",
		T: "실시간항공",
		// L: "노옵션",
		// M: "노팁/노옵션",
		// N: "노팁/노옵션/노쇼핑",
	};

	if (!emphasisCd || !classType[emphasisCd]) return null;
	return classType[emphasisCd];
};

const StartWeek = (props: {outStartWdayPcnt: number; outStartWdayMonList: string}) => {
	const {outStartWdayPcnt} = props;
	const outStartWdayMonList = props.outStartWdayMonList.split("|");
	if (outStartWdayPcnt === 1) {
		/* 1 패턴인경우
		 * 	: 일월화수목금토 -> 매일출발
		 *  : 월수금 -> 월, 수, 금
		 * */
		const wdayString = outStartWdayMonList[0].split(":")[1];
		if (wdayString === "일,월,화,수,목,금,토") return "매일출발";
		return `${wdayString.split(",").join(",")}요일 출발`;
	}
	return "월별 출발요일 상이";
};

const airOrderSort = (props): Array<string> => {
	const trCompanyOrderArr = props.trCompanyList ? props.trCompanyList.split("|") : [];
	const airCdArr = props.airCd.split(",");
	const airNameArr = props.airName.split(",");
	const mergeArray = airNameArr.map((name, idx) => `${name},${airCdArr[idx]}`).sort();
	trCompanyOrderArr.forEach(trComanyString => {
		const index = mergeArray.indexOf(trComanyString);
		index >= 0 && mergeArray.splice(index, 1);
	});
	return trCompanyOrderArr.concat(mergeArray);
};

const GoodsInfoStarIconNm = (reviewScore: number) => {
	const floorScore = Math.floor(reviewScore);
	return reviewScore - floorScore > 0.5 ? `${floorScore}_5` : floorScore;
};

/* 적립 예상 금액 */
const getExpectMileage = (props: Array<EventMileage>) => {
	let adtSumMileage = 0;
	if (!Array.isArray(props)) return adtSumMileage;
	for (const item of props) {
		let adtMlgPoint = Math.round(((item.adtPrice + item.airtaxAdtCost + item.bafAdtCost) * item.mlgPoint) / 100);
		if (item.mlgSaveKindCd !== "R1") {
			adtMlgPoint = item.mlgPoint;
		}
		adtSumMileage += adtMlgPoint;
		// if (item.mlgDivi === 'GNL') {
		//     if (parseInt(item.evStartDt) >= 20180301) {
		//         gnlSaveDt = item.saveDt;
		//     }
		//     mlgSetNo = item.promotionNo;
		// } else {
		//     prmSaveDt = item.saveDt;
		//     mlgPromotionNo = item.promotionNo;
		// }
	}
	return adtSumMileage;
};

const getChdAgeRange = ({evStartDt}: {evStartDt: string}) => {
	const dt = dayjs(evStartDt);
	const startMonth = 12 * 12;
	const endMonth = 12 * 2;
	return [
		dt.add(-startMonth, "month").add(1, "day").format("YYYY.MM.DD"),
		dt.add(-endMonth, "month").format("YYYY.MM.DD"),
	];
};

const getInfAgeRange = ({evStartDt}: {evStartDt: string}) => {
	const dt = dayjs(evStartDt);
	const startMonth = 12 * 2;
	return [dt.add(-startMonth, "month").add(1, "day").format("YYYY.MM.DD"), dt.format("YYYY.MM.DD")];
};

export {
	GoodsInfoStarIconNm,
	StartWeek,
	airOrderSort,
	emphasisCdLabel,
	evProgDiviLabel,
	getChdAgeRange,
	getExpectMileage,
	getInfAgeRange,
	goodsBrandCdLabel,
	goodsTpLabel,
	goodsFlag,
	hashTagManufacture,
};

export type {TypeFlag};
